import { useState } from 'react'
import { api } from '../api/client'
import type { TradeIdea, TradeIdeaDetail } from '../api/client'
import { Dialog } from '../components/common/Dialog'
import { Input, Select } from '../components/common/Controls'
import { geldtext } from './priceFormat'
import { ENDE } from './tradeIdeaVocabulary'
import { fehlertext } from './formHelpers'
import s from './TradeIdeaDialogs.module.scss'

/**
 * Das Schreiben an einer Handelsidee selbst — das Abschließen.
 *
 * Orders und Transaktionen gehören zum Depot und stehen in
 * DepotDialogs.tsx; die Idee zeigt sie nur.
 */

/**
 * Die Idee abschließen.
 *
 * Nur erreichbar, wenn keine Stücke mehr da sind — der Knopf ist sonst
 * gesperrt, und der Server weist es ohnehin ab. Danach lässt sich nichts
 * mehr eintragen: Die Idee ist Historie.
 */
export function AbschliessenDialog({ idee, currency, onFertig, onAbbrechen }: {
  idee: TradeIdeaDetail
  currency?: string
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [ende, setEnde] = useState<'CLOSED' | 'INVALIDATED' | 'EXPIRED'>('CLOSED')
  const [ueberschrift, setUeberschrift] = useState('')
  const [notiz, setNotiz] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  function abschliessen() {
    setLaeuft(true)
    setFehler(null)
    api.tradeIdeaAddUpdate(idee.id, {
      headline: ueberschrift.trim(),
      note: notiz.trim() || null,
      status_after: ende,
    }, currency ? { currency: [currency] } : {})
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }

  const realisiert = Number(idee.position.realized?.amount ?? 0)

  return (
    <Dialog titel={`Abschließen — ${idee.name}`} bestaetigen="Abschließen"
            variant="danger" gesperrt={ueberschrift.trim().length < 3}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={abschliessen} onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Danach lässt sich nichts mehr eintragen — die Idee ist Historie. Für
        einen neuen Anlauf wird eine neue Idee angelegt.
      </p>
      {idee.position.realized && realisiert !== 0 && (
        <p className={s.lage}>
          Realisiert: {geldtext(idee.position.realized)}.
        </p>
      )}
      <Select label="Wie" value={ende}
              onChange={(event) =>
                setEnde(event.target.value as typeof ende)}>
        {Object.entries(ENDE).map(([wert, wort]) => (
          <option key={wert} value={wert}>{wort}</option>
        ))}
      </Select>
      <Input label="Überschrift" value={ueberschrift} autoFocus
             placeholder="Der Ausgang in einer Zeile"
             onChange={(event) => setUeberschrift(event.target.value)} />
      <Input label="Notiz" value={notiz}
             placeholder="optional — Plan und Ausführung im Rückblick"
             onChange={(event) => setNotiz(event.target.value)} />
    </Dialog>
  )
}

/**
 * Einen Vorschlag annehmen: vorgeschlagen → angenommen.
 *
 * Meist tut das der Agent selbst, bevor er ordert; der Knopf ist für den
 * Fall, dass der Mensch schneller ist. Ein Eintrag in der Chronik hält
 * fest, wann und mit welchem Wort.
 *
 * Braucht nur Kennung und Namen: So öffnet ihn auch das Häkchen an der
 * Tafelzeile, ohne dass das Detail geladen wäre.
 */
export function AnnehmenDialog({ idee, currency, onFertig, onAbbrechen }: {
  idee: Pick<TradeIdea, 'id' | 'name'>
  currency?: string
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [ueberschrift, setUeberschrift] = useState('Angenommen')
  const [notiz, setNotiz] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  function annehmen() {
    setLaeuft(true)
    setFehler(null)
    api.tradeIdeaAddUpdate(idee.id, {
      headline: ueberschrift.trim(),
      note: notiz.trim() || null,
      status_after: 'ACTIVE',
    }, currency ? { currency: [currency] } : {})
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }

  return (
    <Dialog titel={`Annehmen — ${idee.name}`} bestaetigen="Annehmen"
            gesperrt={ueberschrift.trim().length < 3}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={annehmen} onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Danach ist die Idee angenommen und wartet auf ihren Einstieg. Gekauft
        wird deshalb noch nichts — das steht in den Stufen.
      </p>
      <Input label="Überschrift" value={ueberschrift} autoFocus
             onChange={(event) => setUeberschrift(event.target.value)} />
      <Input label="Notiz" value={notiz}
             placeholder="optional — warum jetzt"
             onChange={(event) => setNotiz(event.target.value)} />
    </Dialog>
  )
}

/**
 * Einen Vorschlag ablehnen: vorgeschlagen oder angenommen → abgelehnt.
 *
 * Das ist der eine Eingriff des Menschen in das, was der Agent führt: kein
 * Urteil über die Idee, eine Entscheidung über das eigene Geld. Deshalb
 * MIT BEGRÜNDUNG, und die ist Pflicht — ohne sie wüsste man in vier Wochen
 * nicht mehr, warum. Danach ist die Idee Historie; nur ohne Stücke und
 * ohne offene Order, und nur der Mensch: Der MCP-Zugang kann es nicht.
 *
 * Braucht nur Kennung und Namen, wie das Annehmen: Das Stoppzeichen an der
 * Tafelzeile öffnet ihn, ohne dass das Detail geladen wäre.
 */
export function AblehnenDialog({ idee, currency, onFertig, onAbbrechen }: {
  idee: Pick<TradeIdea, 'id' | 'name'>
  currency?: string
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [begruendung, setBegruendung] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  function ablehnen() {
    setLaeuft(true)
    setFehler(null)
    api.tradeIdeaAddUpdate(idee.id, {
      headline: 'Abgelehnt',
      note: begruendung.trim(),
      status_after: 'REJECTED',
    }, currency ? { currency: [currency] } : {})
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }

  return (
    <Dialog titel={`Ablehnen — ${idee.name}`} bestaetigen="Ablehnen"
            variant="danger" gesperrt={begruendung.trim().length < 5}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={ablehnen} onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Die Idee ist danach Historie, und der Agent kann zu diesem Titel
        wieder etwas Neues vorschlagen. Die Begründung steht in der Chronik.
      </p>
      <Input label="Begründung" value={begruendung} autoFocus
             placeholder="Warum nicht — etwa: Branche ausgeschlossen, zu riskant, Klumpen im Depot"
             onChange={(event) => setBegruendung(event.target.value)} />
    </Dialog>
  )
}
